<template>
  <div class="input" :class="[size, { centerButton: centerButton }]">
    <!-- Title -->
    <span v-show="title" :lastKey="title" class="input__title">{{
      title
    }}</span>

    <!-- NUMBER TYPE -->
    <div v-show="type === 'number'" class="input__number">
      <div class="input__number__top">
        <!-- Minus button with press-and-hold -->
        <button
          class="input__operation minus"
          @mousedown="startPress(-step)"
          @mouseup="endPress"
          @mouseleave="endPress"
        >
          <img
            @load="resolveAsset($event)"
            :src="minusIcon"
            alt=""
            class="input__operation__icon"
          />
        </button>

        <!-- Main numeric input -->
        <input
          type="number"
          :value="tempInputValue"
          :step="convertedStep"
          :min="convertedMin"
          :max="convertedMax"
          @focus="onFocus"
          @input="onManualInput($event.target.value)"
          @blur="onBlur"
          class="input__number__top__element"
        />

        <!-- Units (read-only button) -->
        <button
          v-show="units"
          class="input__operation units"
          @click="$emit('units')"
        >
          <span :lastKey="units" class="input__operation__text">{{
            units
          }}</span>
        </button>

        <!-- Plus button with press-and-hold -->
        <button
          class="input__operation plus"
          @mousedown="startPress(step)"
          @mouseup="endPress"
          @mouseleave="endPress"
        >
          <img
            @load="resolveAsset($event)"
            :src="plusIcon"
            alt=""
            class="input__operation__icon"
          />
        </button>

        <!-- Center button (optional) -->
        <button
          v-show="centerButton"
          class="input__operation centerButton"
          @click="$emit('center')"
        >
          <span
            :lastKey="getLiteral('globals.center')"
            class="input__operation__text"
          >
            {{ getLiteral('globals.centrar') }}
          </span>
        </button>
      </div>

      <!-- Slider below the numeric input -->
      <Slider
        v-show="slider"
        class="input__number__slider"
        :activeColor="sliderActiveColor"
        :inactiveColor="sliderInactiveColor"
        :valorSel="localValue"
        :units="units"
        :step="step"
        :min="min"
        :max="max"
        @updateLocal="updateLocalFromSlider"
        @valor="commitSliderValue"
      />
    </div>

    <!-- TEXT TYPE -->
    <div v-show="type === 'text'" class="input__text">
      <button v-show="icon" class="input__operation search">
        <img
          @load="resolveAsset($event)"
          :src="searchIcon"
          alt=""
          class="input__operation__icon"
        />
      </button>

      <span
        v-show="!valorSel"
        :lastKey="placeholder"
        class="input__text__placeholder"
        :style="{ left: icon ? '40px' : '10px' }"
      >
        {{ placeholder }}
      </span>

      <input
        type="text"
        class="input__text__element"
        :value="valorSel"
        @input="onTextInput($event)"
      />
    </div>

    <!-- COLOR TYPE -->
    <input
      v-show="type === 'color'"
      type="color"
      class="input__color"
      :value="valorSel"
      @input="onColorInput($event)"
    />
  </div>
</template>

<script>
import Slider from '@/components/library/Slider.vue';

export default {
  name: 'Input',
  components: { Slider },
  props: {
    referencia: String,
    centerButton: { type: Boolean, default: false },
    placeholder: { type: String, default: null },
    borderRadius: { type: String, default: '0px' },
    size: { type: String, default: 'md' },
    title: { type: String, default: null },
    units: { type: String, default: null }, // e.g. 'mm', 'cm', 'm', 'ud'
    type: { type: String, default: 'text' }, // 'text', 'number', 'color'
    sliderActiveColor: { type: String, default: 'black' },
    sliderInactiveColor: { type: String, default: 'transparent' },
    minusIcon: {
      type: String,
      default: () => require('@/assets/images/iconos/minus.svg'),
    },
    plusIcon: {
      type: String,
      default: () => require('@/assets/images/iconos/plus.svg'),
    },
    icon: { type: Boolean, default: true },
    searchIcon: {
      type: String,
      default: () => require('@/assets/images/iconos/search.svg'),
    },
    slider: { type: Boolean, default: false },
    valorSel: {
      // For numeric usage, treat as the base value.
      type: [Number, String],
      default: 0,
    },
    step: { type: Number, default: 1 },
    min: { type: Number, default: 0 },
    max: { type: Number, default: 10 },
  },
  data() {
    return {
      localValue: this.valorSel, // internal numeric value
      clickTimer: null,
      holdTimer: null,
      holdStarted: false,
      currentHoldValue: null,
      isFocused: false,

      // tempInputValue: raw text typed into the numeric input
      tempInputValue: '',

      // lastEmittedValue: used to prevent re-emitting the same value
      lastEmittedValue:
        this.type === 'number' ? Number(this.valorSel) : String(this.valorSel),
    };
  },
  watch: {
    referencia: {
      handler(newVal, oldVal) {
        if (newVal == 'personalizado') {
          if (this.valorSel < this.min || this.valorSel > this.max) {
            this.$emit('valor', this.lastEmittedValue);
          }
        }
      },
      deep: true,
    },
    // When valorSel changes externally, update localValue and run correction.
    valorSel(newVal) {
      if (newVal !== this.localValue) {
        this.localValue = newVal;
        this.$nextTick(() => {
          this.checkAndCorrect();
        });
      }
    },
    // Keep tempInputValue in sync when not focused.
    localValue(newVal) {
      if (!this.isFocused && this.type === 'number') {
        this.tempInputValue = this.formatDisplayValue(newVal);
      }
    },
    min() {
      if (!this.isFocused && this.type === 'number') {
        this.checkAndCorrect();
      }
    },
    max() {
      if (!this.isFocused && this.type === 'number') {
        this.checkAndCorrect();
      }
    },
  },
  computed: {
    convertedStep() {
      switch (this.units) {
        case 'mm':
          return this.step * 1000;
        case 'cm':
          return this.step * 100;
        default:
          return this.step;
      }
    },
    convertedMin() {
      switch (this.units) {
        case 'mm':
          return this.min * 1000;
        case 'cm':
          return this.min * 100;
        default:
          return this.min;
      }
    },
    convertedMax() {
      switch (this.units) {
        case 'mm':
          return this.max * 1000;
        case 'cm':
          return this.max * 100;
        default:
          return this.max;
      }
    },
    displayedLocalValue() {
      let displayVal;
      switch (this.units) {
        case 'mm':
          displayVal = this.localValue * 1000;
          break;
        case 'cm':
          displayVal = this.localValue * 100;
          break;
        default:
          displayVal = this.localValue;
      }
      return String(Math.round(displayVal * 1000) / 1000);
    },
  },
  mounted() {
    if (this.type === 'number') {
      this.tempInputValue = this.displayedLocalValue;
    }
  },
  methods: {
    /**
     * Emit only if the new value differs from the lastEmittedValue.
     */
    emitValorIfChanged(newVal) {
      if (this.type === 'number') {
        const numericLast = Number(this.lastEmittedValue);
        const numericNew = Number(newVal);
        if (numericLast === numericNew) return;
        this.$emit('valor', numericNew);
        this.lastEmittedValue = numericNew;
      } else {
        const strLast = String(this.lastEmittedValue);
        const strNew = String(newVal);
        if (strLast === strNew) return;
        this.$emit('valor', strNew);
        this.lastEmittedValue = strNew;
      }
    },

    parseToBaseValue(str) {
      const raw = Number(str);
      if (isNaN(raw)) return null;
      switch (this.units) {
        case 'mm':
          return raw / 1000;
        case 'cm':
          return raw / 100;
        default:
          return raw;
      }
    },
    formatDisplayValue(baseVal) {
      let displayVal;
      switch (this.units) {
        case 'mm':
          displayVal = baseVal * 1000;
          break;
        case 'cm':
          displayVal = baseVal * 100;
          break;
        default:
          displayVal = baseVal;
      }
      return String(Math.round(displayVal * 1000) / 1000);
    },

    // Numeric input typing
    onManualInput(displayVal) {
      this.tempInputValue = displayVal;
      const baseVal = this.parseToBaseValue(displayVal);
      // Only update localValue if valid and in range
      if (baseVal !== null && baseVal >= this.min && baseVal <= this.max) {
        this.localValue = baseVal;
        this.emitValorIfChanged(baseVal);
      }
    },

    onFocus() {
      this.isFocused = true;
      if (this.type === 'number') {
        this.tempInputValue = this.displayedLocalValue;
      }
    },

    onBlur() {
      this.isFocused = false;
      if (this.type !== 'number') return;
      const baseVal = this.parseToBaseValue(this.tempInputValue);
      if (baseVal === null) {
        // revert to displayed value
        this.tempInputValue = this.displayedLocalValue;
      } else if (baseVal < this.min) {
        this.localValue = this.min;
        this.emitValorIfChanged(this.min);
        this.tempInputValue = this.formatDisplayValue(this.min);
      } else if (baseVal > this.max) {
        this.localValue = this.max;
        this.emitValorIfChanged(this.max);
        this.tempInputValue = this.formatDisplayValue(this.max);
      } else {
        this.localValue = baseVal;
        this.emitValorIfChanged(baseVal);
        this.tempInputValue = this.formatDisplayValue(baseVal);
      }
    },

    /**
     * Corrects localValue to be within [min, max]. If referencia is "personalizado",
     * the new value is emitted; otherwise, only the displayed value is updated.
     */
    checkAndCorrect() {
      // Force localValue to be within [min, max]
      if (this.localValue < this.min) {
        this.localValue = this.min;
      } else if (this.localValue > this.max) {
        this.localValue = this.max;
      }

      // Update numeric display
      if (this.type === 'number') {
        this.tempInputValue = this.formatDisplayValue(this.localValue);
      }

      // Only emit if referencia === "personalizado"
      if (this.referencia === 'personalizado') {
        this.emitValorIfChanged(this.localValue);
      } else {
        // But ALWAYS update lastEmittedValue so it matches the corrected localValue
        if (this.type === 'number') {
          this.lastEmittedValue = Number(this.localValue);
        } else {
          this.lastEmittedValue = String(this.localValue);
        }
      }
    },

    // Press-and-hold functions
    startPress(delta) {
      this.currentHoldValue = delta;
      this.holdStarted = false;
      this.clickTimer = setTimeout(() => {
        this.holdStarted = true;
        this.setLocalValor(this.currentHoldValue);
        this.holdTimer = setInterval(() => {
          this.setLocalValor(this.currentHoldValue);
        }, 100);
      }, 200);
    },
    endPress() {
      if (this.currentHoldValue === null) return;
      if (this.clickTimer) {
        clearTimeout(this.clickTimer);
        this.clickTimer = null;
      }
      if (!this.holdStarted) {
        this.setLocalValor(this.currentHoldValue);
      }
      if (this.holdTimer) {
        clearInterval(this.holdTimer);
        this.holdTimer = null;
      }
      this.holdStarted = false;
      this.currentHoldValue = null;
      if (this.type === 'number') {
        this.tempInputValue = this.formatDisplayValue(this.localValue);
      }
      this.emitValorIfChanged(this.localValue);
    },
    setLocalValor(delta) {
      const newValue = parseFloat(
        (Number(this.localValue) + delta).toFixed(10)
      );
      if (newValue <= this.max && newValue >= this.min) {
        this.localValue = newValue;
        // Emission deferred until endPress
      }
    },

    // Slider event handlers
    updateLocalFromSlider(sliderVal) {
      this.localValue = sliderVal;
    },
    commitSliderValue(sliderVal) {
      this.localValue = sliderVal;
      this.emitValorIfChanged(sliderVal);
      if (!this.isFocused && this.type === 'number') {
        this.tempInputValue = this.formatDisplayValue(sliderVal);
      }
    },

    // Text input handler
    onTextInput(e) {
      const newVal = String(e.target.value);
      this.emitValorIfChanged(newVal);
    },

    // Color input handler
    onColorInput(e) {
      const newVal = String(e.target.value);
      this.emitValorIfChanged(newVal);
    },

    // These methods remain defined only in the template:
    // resolveAsset() {}
    // getLiteral() {}
  },
};
</script>

<style scoped lang="scss">
.input {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 300px;
  width: 100%;

  &__title {
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 10px;
  }

  &__number {
    display: flex;
    flex-direction: column;

    &__top {
      display: flex;
      flex-direction: row;

      &__element {
        appearance: none;
        outline: none;
        border: none;
        background-color: white;
        text-align: center;
        width: calc(100% - calc(30px * 2));
        height: 30px;
        border-radius: 0px;
        font-size: 13px;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }

    &__slider {
    }
  }

  &__text {
    display: flex;
    align-items: center;
    position: relative;
    flex-direction: row;

    &__element {
      appearance: none;
      outline: none;
      border: none;
      border-radius: 0px v-bind(borderRadius) v-bind(borderRadius) 0px;
      background-color: white;
      text-align: start;
      width: 100%;
      height: 30px;
      padding: 0px 10px;
      font-size: 13px;
    }

    &__placeholder {
      pointer-events: none;
      font-size: 13px;
      color: rgb(190, 190, 190);
      position: absolute;
    }
  }

  &__operation {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 30px;
    border: none;
    background-color: white;

    &__icon {
      opacity: 0;
      width: 15px;
      height: 15px;
      object-fit: contain;
      transition: opacity 0.35s ease-in-out;
    }

    &__text {
      color: black;
      font-size: 13px;
    }

    &.search {
      width: 30px;
      border-radius: v-bind(borderRadius) 0px 0px v-bind(borderRadius);
      border-right: 1px solid #d7dad7;
    }

    &.minus {
      width: 30px;
      border-radius: v-bind(borderRadius) 0px 0px v-bind(borderRadius);
      border-right: 1px solid #d7dad7;
    }

    &.units {
      pointer-events: none;
      border-radius: 0px;
      width: auto;
      border-left: 1px solid #d7dad7;
    }

    &.plus {
      width: 30px;
      border-radius: 0px v-bind(borderRadius) v-bind(borderRadius) 0px;
      border-left: 1px solid #d7dad7;
    }

    &.centerButton {
      cursor: pointer;
      border-radius: 0px v-bind(borderRadius) v-bind(borderRadius) 0px;
      width: auto;
      border-left: 1px solid #d7dad7;
    }
  }

  &__color {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 75px;
    height: 30px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    margin: 0px;
    padding: 0px;

    &::-webkit-color-swatch {
      border-radius: 5px;
      border: none;
    }

    &::-moz-color-swatch {
      border-radius: 5px;
      border: none;
    }
  }

  &.centerButton {
    & .input__operation.plus {
      border-radius: 0px;
    }
  }

  &.sm {
    max-width: 150px;
  }

  &.lg {
    max-width: 450px;
  }
}
</style>
